import { AlertCircle, CheckCircle, Clock, Target } from 'lucide-react';

import type { StrategicRecommendation, ActionItem } from '../types/report';
import { cn } from '../lib/utils';

interface StrategyCardProps {
  recommendation: StrategicRecommendation;
}

const priorityConfig = {
  HIGH: {
    icon: AlertCircle,
    label: '긴급',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    textColor: 'text-red-700',
    badgeColor: 'bg-red-100 text-red-800',
  },
  MEDIUM: {
    icon: Clock,
    label: '중요',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    textColor: 'text-yellow-700',
    badgeColor: 'bg-yellow-100 text-yellow-800',
  },
  LOW: {
    icon: CheckCircle,
    label: '권장',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    textColor: 'text-green-700',
    badgeColor: 'bg-green-100 text-green-800',
  },
};

const ActionItemCard = ({ item }: { item: ActionItem }) => {
  const config = priorityConfig[item.priority];
  const Icon = config.icon;

  return (
    <div className={cn('rounded-lg border p-4', config.bgColor, config.borderColor)}>
      <div className="flex items-start gap-3">
        <Icon className={cn('h-5 w-5 mt-0.5', config.textColor)} />
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className={cn('px-2 py-0.5 rounded text-xs font-medium', config.badgeColor)}>{config.label}</span>
            <h4 className="font-medium text-gray-900">{item.action}</h4>
          </div>
          <p className="text-sm text-gray-600 mb-2">{item.expectedImpact}</p>
          {item.targetBranches && (
            <div className="text-xs text-gray-500">
              <span className="font-medium">대상 지점: </span>
              {item.targetBranches}
            </div>
          )}
          {item.note && (
            <div className="text-xs text-gray-500 mt-1">
              <span className="font-medium">참고: </span>
              {item.note}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const StrategyCard = ({ recommendation }: StrategyCardProps) => {
  const highPriority = recommendation.actionItems.filter((item) => item.priority === 'HIGH');
  const mediumPriority = recommendation.actionItems.filter((item) => item.priority === 'MEDIUM');
  const lowPriority = recommendation.actionItems.filter((item) => item.priority === 'LOW');

  return (
    <div className="space-y-6">
      <div className="rounded-lg border-2 border-blue-200 bg-blue-50 p-4">
        <div className="flex items-center gap-2 mb-2">
          <Target className="h-5 w-5 text-blue-600" />
          <h3 className="font-bold text-blue-900">전략 요약</h3>
        </div>
        <p className="text-sm text-blue-800 leading-relaxed">{recommendation.summary}</p>
      </div>

      <div className="space-y-4">
        <h3 className="font-semibold text-gray-900">실행 과제</h3>

        {highPriority.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-red-700">긴급 (HIGH)</h4>
            {highPriority.map((item, index) => (
              <ActionItemCard key={index} item={item} />
            ))}
          </div>
        )}

        {mediumPriority.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-yellow-700">중요 (MEDIUM)</h4>
            {mediumPriority.map((item, index) => (
              <ActionItemCard key={index} item={item} />
            ))}
          </div>
        )}

        {lowPriority.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-green-700">권장 (LOW)</h4>
            {lowPriority.map((item, index) => (
              <ActionItemCard key={index} item={item} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
